import React, { useCallback, useEffect, useState } from 'react';
import { Button, DatePicker, Table } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../utils/currency';
import { formatProductDimensions } from '../utils/productDimensions';
import { getApiErrorMessage } from '../utils/apiError';
import { formatQuantitySet } from '../utils/commercialQuantity';
import { useFeedback } from '../utils/feedback';
import {
  fetchMonthlyInventoryReport,
} from '../modules/inventory/api/inventoryApi';
import type {
  InventoryVariantReport,
  MonthlyInventoryReport,
  QuantityReportBucket,
} from '../dto/inventory';
import { DatabaseOutlined, ExportOutlined, ImportOutlined, SwapOutlined } from '@ant-design/icons';
import { ReportFigure, ReportSection } from '../modules/reports/ReportTab';
import { ObjectCardEmpty } from '../components/layout/ObjectCard';
import './InventoryReports.css';

function quantityCell(
  bucket: QuantityReportBucket,
  language: string,
): React.ReactNode {
  return (
    <div className="inventory-report__bucket">
      <span>
        {/* Each unit stays whole; a narrow column breaks only between units. */}
        {String(formatQuantitySet(bucket, 'quantity', language, {
          fallbackQuantity: bucket.quantity,
          fallbackUnit: bucket.quantityUnit,
        })).split(' / ').map((part, index) => (
          <React.Fragment key={index}>
            {index > 0 && ' / '}
            <span className="inventory-report__unit">{part}</span>
          </React.Fragment>
        ))}
      </span>
      <span className="report-muted">{formatCurrency(Number(bucket.valuePLN || 0), 'PLN')}</span>
    </div>
  );
}

const InventoryReports: React.FC = () => {
  const { t, i18n } = useTranslation(['inventory', 'common']);
  const feedback = useFeedback();
  const [selectedMonth, setSelectedMonth] = useState<Dayjs>(dayjs().subtract(1, 'month'));
  const [report, setReport] = useState<MonthlyInventoryReport | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      setReport(await fetchMonthlyInventoryReport(
        selectedMonth.year(),
        selectedMonth.month() + 1,
      ));
    } catch (error: unknown) {
      setReport(null);
      feedback.error(getApiErrorMessage(error, t, 'inventory:error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [feedback, selectedMonth, t]);

  useEffect(() => {
    void fetchReport();
  }, [fetchReport]);

  const columns = [
    {
      title: t('inventory:fields.productName'),
      key: 'product',
      width: 200,
      render: (_: unknown, row: InventoryVariantReport) => {
        const dimensions = formatProductDimensions(row);
        return (
          <div className="inventory-report__product">
            <span className="inventory-report__name report-cut" title={row.productName || undefined}>
              {row.productName || t('inventory:fields.productName')}
            </span>
            {dimensions && (
              <span className="report-cut report-muted" title={dimensions} data-testid={`inventory-report-dimensions-${row.productVariantId}`}>{dimensions}</span>
            )}
            <span className="report-cut report-muted" title={[row.variantName, row.sku].filter(Boolean).join(' · ')}>
              {[row.variantName, row.sku].filter(Boolean).join(' · ') || '—'}
            </span>
          </div>
        );
      },
    },
    {
      title: t('inventory:reports.openingStock'),
      dataIndex: 'opening',
      key: 'opening',
      render: (bucket: QuantityReportBucket) => quantityCell(bucket, i18n.language),
    },
    {
      title: t('inventory:reports.receipts'),
      dataIndex: 'receipts',
      key: 'receipts',
      render: (bucket: QuantityReportBucket) => quantityCell(bucket, i18n.language),
    },
    {
      title: t('inventory:reports.releases'),
      dataIndex: 'releases',
      key: 'releases',
      render: (bucket: QuantityReportBucket) => quantityCell(bucket, i18n.language),
    },
    {
      title: t('inventory:reports.adjustments'),
      dataIndex: 'adjustments',
      key: 'adjustments',
      render: (bucket: QuantityReportBucket) => quantityCell(bucket, i18n.language),
    },
    {
      title: t('inventory:reports.closingStock'),
      dataIndex: 'closing',
      key: 'closing',
      render: (bucket: QuantityReportBucket) => quantityCell(bucket, i18n.language),
    },
  ];

  const summary = report?.summary;
  const variants = report?.variants || [];
  // A missing summary is „—”, never 0 zł.
  // While another month loads, the tiles do not keep the previous month's amounts.
  const money = (value: number | string | null | undefined) => {
    if (loading) return '…';
    return value === null || value === undefined ? '—' : formatCurrency(Number(value), 'PLN');
  };
  const periodLabel = report?.periodStart && report?.periodEnd
    ? `${dayjs(report.periodStart).format('DD.MM.YYYY')} – ${dayjs(report.periodEnd).format('DD.MM.YYYY')}`
    : selectedMonth.format('MM.YYYY');

  return (
    <div className="report-tab inventory-report">
      <div className="report-controls">
        <DatePicker
          picker="month"
          value={selectedMonth}
          format="MM.YYYY"
          allowClear={false}
          aria-label={t('inventory:reports.selectMonth')}
          onChange={(date) => date && setSelectedMonth(date)}
          disabledDate={(current) => Boolean(current && current > dayjs().endOf('month'))}
        />
        <div className="report-controls__end">
          <span className="report-controls__counter">
            {t('inventory:reports.counter', { period: periodLabel })}
          </span>
        </div>
      </div>

      {!loading && (!report || variants.length === 0) ? (
        <ObjectCardEmpty
          icon={<SwapOutlined />}
          text={t('inventory:reports.noDataMonth', { month: selectedMonth.format('MM.YYYY') })}
          action={(
            <Button onClick={() => setSelectedMonth(selectedMonth.subtract(1, 'month'))}>
              {t('inventory:reports.previousMonth')}
            </Button>
          )}
        />
      ) : (
        <>
          <div className="report-figures">
            <ReportFigure
              icon={<DatabaseOutlined />}
              tone="gray"
              label={t('inventory:reports.openingBalance')}
              value={money(summary?.openingBalancePLN)}
              note={t('inventory:reports.openingNote')}
            />
            <ReportFigure
              icon={<ImportOutlined />}
              tone="green"
              label={t('inventory:reports.totalReceiptsValue')}
              value={money(summary?.receiptsPLN)}
              note={t('inventory:reports.receiptsNote')}
            />
            <ReportFigure
              icon={<ExportOutlined />}
              tone="orange"
              label={t('inventory:reports.totalReleasesValue')}
              value={money(summary?.releasesPLN)}
              note={t('inventory:reports.releasesNote')}
            />
            <ReportFigure
              icon={<DatabaseOutlined />}
              tone="blue"
              label={t('inventory:reports.closingBalance')}
              value={money(summary?.closingBalancePLN)}
              note={t('inventory:reports.closingNote')}
            />
          </div>

          <ReportSection
            icon={<SwapOutlined />}
            tone="teal"
            title={t('inventory:reports.quantityLedger')}
            count={variants.length}
            lead={t('inventory:reports.quantityLedgerLead')}
          >
            <Table
              className="report-table"
              dataSource={variants}
              columns={columns}
              rowKey="productVariantId"
              loading={loading}
              pagination={{ showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], hideOnSinglePage: true }}
              tableLayout="fixed"
              locale={{ emptyText: t('inventory:reports.noData') }}
            />
          </ReportSection>
        </>
      )}
    </div>
  );
};

export default InventoryReports;
